import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CalendarDays, Plus, X,
  Layers, CheckCircle2, Clock, XCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  useToast, useConfirm,
  EmptyState, PageHeader, StatusBadge, Button, Input,
} from '../components/ui';

const reservationStatusVariant = (status) => {
  switch (status) {
    case 'pending':   return 'warning';
    case 'confirmed': return 'primary';
    case 'seated':    return 'success';
    case 'completed': return 'neutral';
    case 'cancelled': return 'danger';
    case 'no-show':   return 'danger';
    default:          return 'neutral';
  }
};

const STAT_DEFS = [
  { key: 'total',     label: 'Total',     color: 'primary', Icon: Layers       },
  { key: 'pending',   label: 'Pending',   color: 'warning', Icon: Clock        },
  { key: 'confirmed', label: 'Confirmed', color: 'primary', Icon: CalendarDays },
  { key: 'seated',    label: 'Seated',    color: 'success', Icon: CheckCircle2 },
  { key: 'cancelled', label: 'Cancelled', color: 'danger',  Icon: XCircle      },
];

const TINT = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger:  'bg-danger-50 text-danger-600',
};

// Pre-compute half-hour slots from 00:00 to 23:30
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const hour = String(Math.floor(i / 2)).padStart(2, '0');
  const minute = i % 2 === 0 ? '00' : '30';
  return `${hour}:${minute}`;
});

const getStatusLabel = (status) => {
  switch (status) {
    case 'pending':   return 'Pending';
    case 'confirmed': return 'Confirmed';
    case 'seated':    return 'Seated';
    case 'completed': return 'Done';
    case 'cancelled': return 'Cancelled';
    case 'no-show':   return 'No-show';
    default:          return status;
  }
};

const Reservations = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [reservations, setReservations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [formData, setFormData] = useState({
    customerName: '',
    phoneNumber: '',
    email: '',
    partySize: 2,
    reservationTime: '19:00',
    specialRequests: '',
    tableIds: [],
  });

  const canEdit = ['super_admin', 'admin', 'manager', 'cashier', 'waiter'].includes(user?.role);

  useEffect(() => {
    fetchReservations();
  }, [selectedDate]);

  const fetchReservations = async () => {
    try {
      const query = selectedDate ? `?date=${selectedDate}` : '';
      const res = await axios.get(`/api/reservations${query}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setReservations(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReservation) {
        await axios.put(`/api/reservations/${editingReservation._id}/status`, {
          status: formData.status
        }, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
      } else {
        await axios.post('/api/reservations', formData, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
      }
      setShowModal(false);
      setEditingReservation(null);
      setFormData({
        customerName: '', phoneNumber: '', email: '',
        partySize: 2, reservationTime: '19:00',
        specialRequests: '', tableIds: [],
      });
      fetchReservations();
      toast.success(editingReservation ? 'Reservation updated' : 'Reservation created');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error saving reservation');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`/api/reservations/${id}/status`, { status }, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      fetchReservations();
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const deleteReservation = async (id) => {
    const ok = await confirm({
      title: 'Cancel reservation?',
      message: 'The reservation will be cancelled and the table freed.',
      variant: 'danger',
      confirmText: 'Cancel reservation',
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/reservations/${id}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      toast.success('Reservation cancelled');
      fetchReservations();
    } catch (err) {
      toast.error('Error cancelling reservation');
    }
  };

  const openEditModal = (reservation) => {
    setEditingReservation(reservation);
    setFormData({
      customerName: reservation.customerName,
      phoneNumber: reservation.phoneNumber,
      email: reservation.email || '',
      partySize: reservation.partySize,
      reservationTime: reservation.reservationTime,
      specialRequests: reservation.specialRequests || '',
      tableIds: reservation.tables?.map(t => t._id) || [],
      status: reservation.status,
    });
    setShowModal(true);
  };

  const statValue = (key) => {
    if (key === 'total')     return reservations.length;
    if (key === 'cancelled') return reservations.filter(r => ['cancelled', 'no-show'].includes(r.status)).length;
    return reservations.filter(r => r.status === key).length;
  };

  return (
    <>
      <PageHeader
        title="Reservations"
        subtitle="View, confirm, and manage table bookings by date."
        action={canEdit && (
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => {
              setEditingReservation(null);
              setFormData({
                customerName: '', phoneNumber: '', email: '',
                partySize: 2, reservationTime: '19:00',
                specialRequests: '', tableIds: [],
              });
              setShowModal(true);
            }}
          >
            New Reservation
          </Button>
        )}
      />

      {/* Date Picker */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 mb-6 flex items-end gap-4">
        <div className="w-56">
          <Input
            label="Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            leftIcon={<CalendarDays size={14} />}
          />
        </div>
        <p className="text-sm text-neutral-500 pb-2">
          Showing <span className="font-semibold text-neutral-700">{reservations.length}</span> reservation{reservations.length === 1 ? '' : 's'} for this date
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {STAT_DEFS.map(({ key, label, color, Icon }) => (
          <div key={key} className="bg-white rounded-lg border border-neutral-200 shadow-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${TINT[color]}`}>
                <Icon size={18} />
              </div>
            </div>
            <p className="text-3xl font-bold text-neutral-900 tabular-nums">{statValue(key)}</p>
          </div>
        ))}
      </div>

      {/* Reservations Table */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Party Size</th>
              <th>Time</th>
              <th>Tables</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reservations.length === 0 && (
              <tr>
                <td colSpan="6">
                  <EmptyState
                    icon={CalendarDays}
                    title="No reservations for this date"
                    message="Try picking a different date above, or add a new reservation."
                  />
                </td>
              </tr>
            )}
            {reservations.map(reservation => (
              <tr key={reservation._id}>
                <td>
                  <p className="font-semibold">{reservation.customerName}</p>
                  <p className="text-xs text-neutral-500">{reservation.phoneNumber}</p>
                </td>
                <td>{reservation.partySize} guests</td>
                <td>
                  {new Date(reservation.reservationDate).toLocaleDateString()}
                  <span className="text-neutral-400 mx-1">·</span>
                  <span className="font-medium">{reservation.reservationTime}</span>
                </td>
                <td className="text-sm text-neutral-600">
                  {reservation.tables?.length
                    ? reservation.tables.map(t => `#${t.number}`).join(', ')
                    : <span className="text-neutral-400">No tables</span>}
                </td>
                <td>
                  <StatusBadge variant={reservationStatusVariant(reservation.status)} dot>
                    {getStatusLabel(reservation.status)}
                  </StatusBadge>
                </td>
                <td>
                  <div className="flex flex-wrap gap-2">
                    {reservation.status === 'pending' && (
                      <Button size="sm" variant="primary" onClick={() => updateStatus(reservation._id, 'confirmed')}>
                        Confirm
                      </Button>
                    )}
                    {reservation.status === 'confirmed' && (
                      <Button size="sm" variant="success" onClick={() => updateStatus(reservation._id, 'seated')}>
                        Seat Party
                      </Button>
                    )}
                    {reservation.status === 'seated' && (
                      <Button size="sm" variant="secondary" onClick={() => updateStatus(reservation._id, 'completed')}>
                        Complete
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => openEditModal(reservation)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteReservation(reservation._id)}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-neutral-900">
                {editingReservation ? 'Edit reservation' : 'New reservation'}
              </h2>
              <button
                type="button"
                onClick={() => { setShowModal(false); setEditingReservation(null); }}
                className="text-neutral-400 hover:text-neutral-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <Input
                  label="Customer name"
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  required
                />
                <Input
                  label="Phone number"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  required
                />
                <Input
                  label="Email (optional)"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Party size"
                    type="number"
                    min="1"
                    value={formData.partySize}
                    onChange={(e) => setFormData({ ...formData, partySize: e.target.value })}
                    required
                  />
                  <div>
                    <label className="text-sm font-medium text-neutral-700 block mb-1">Time</label>
                    <select
                      value={formData.reservationTime}
                      onChange={(e) => setFormData({ ...formData, reservationTime: e.target.value })}
                      className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                    >
                      {TIME_SLOTS.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Special requests (optional)</label>
                  <textarea
                    value={formData.specialRequests}
                    onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                    rows="2"
                    className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 resize-none"
                  />
                </div>
                {editingReservation && (
                  <div>
                    <label className="text-sm font-medium text-neutral-700 block mb-1">Status</label>
                    <select
                      value={formData.status || ''}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="seated">Seated</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="no-show">No Show</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2 sticky bottom-0">
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => { setShowModal(false); setEditingReservation(null); }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" fullWidth>
                  {editingReservation ? 'Update reservation' : 'Create reservation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Reservations;
